import renderFieldComponent from './FieldRenderer';
import { FieldError, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { TFormDefinition, TFormFieldDefinition } from './types';
import { Stack } from '@/ui/primatives';
import Button from '@/ui/Button';

interface DynamicFormProps {
  schema: TFormDefinition;
  submitText?: string;
  onSubmit: (data: any) => void;
  additionalFields?: {
    [key: string]: TFormFieldDefinition;
  };
}

const DynamicForm: React.FC<DynamicFormProps> = ({ schema, onSubmit, additionalFields = {}, submitText = 'Submit' }) => {
  const formSchema = Yup.object().shape(
    Object.entries({ ...schema.fields, ...additionalFields }).reduce((acc, [name, field]) => {
      let fieldSchema = Yup.string();

      if (field.required) {
        fieldSchema = fieldSchema.required('Required');
      }

      if (field.minLength) {
        fieldSchema = fieldSchema.min(field.minLength, `Must be at least ${field.minLength} characters`);
      }

      if (field.maxLength) {
        fieldSchema = fieldSchema.max(field.maxLength, `Cannot exceed ${field.maxLength} characters`);
      }

      if (field.type === 'email') {
        fieldSchema = fieldSchema.email('Must be a valid email address');
      }

      return { ...acc, [name]: fieldSchema };
    }, {})
  );

  type FormValues = Record<string, string | number | boolean>;

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(formSchema),
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={20}>
        {Object.entries(schema.fields).sort((a, b) => a[1].fieldIndex - b[1].fieldIndex).map(([name, field]) =>
          renderFieldComponent({
            name,
            field,
            control,
            register,
            error: errors[name] as FieldError,
          })
        )}
        {additionalFields && Object.entries(additionalFields).sort((a, b) => a[1].fieldIndex - b[1].fieldIndex).map(([name, field]) =>
          renderFieldComponent({
            name,
            field,
            control,
            register,
            error: errors[name] as FieldError,
          })
        )}
        <Button.Black small type="submit">{submitText}</Button.Black>
      </Stack>
    </form>
  );
};

export default DynamicForm;
